'use client';

import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  CreditCard,
  MapPin,
  MessageCircle,
  Settings,
  Users,
} from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import { Avatar, BrowserFrame } from './demo-kit';

/*
 * Shared chrome for the desktop admin demos (calendar, CRM, programs).
 * Everything is designed at a fixed 760 x 480 including the browser frame,
 * which leaves a 706 x 398 content area beside the rail and under the top
 * bar. The flex row follows the page direction, so the rail sits on the
 * right in Arabic.
 */

export { ADMIN_DEMO_SIZE } from './demo-sizes';

/** Content area available to `children`, in pixels. */
export const ADMIN_CONTENT_SIZE = { width: 706, height: 398 } as const;

const RAIL_ITEMS = [
  { key: 'calendar', Icon: CalendarDays },
  { key: 'customers', Icon: Users },
  { key: 'payments', Icon: CreditCard },
  { key: 'reports', Icon: BarChart3 },
  { key: 'inbox', Icon: MessageCircle },
  { key: 'settings', Icon: Settings },
] as const;

export type AdminSection = (typeof RAIL_ITEMS)[number]['key'];

const DEFAULT_LOCATION = { en: 'Northside · Main', ar: 'نورثسايد · الفرع الرئيسي' } as const;

export function AdminShell({
  active,
  title,
  subtitle,
  locale,
  location,
  url = 'app.northsidepadel.com',
  actions,
  className,
  children,
}: React.PropsWithChildren<{
  active: AdminSection;
  title: string;
  subtitle?: string;
  locale: MarketingLocale;
  /** Location switcher label; defaults to the fictional Northside venue. */
  location?: string;
  url?: string;
  /** Extra controls rendered at the end of the top bar. */
  actions?: React.ReactNode;
  className?: string;
}>) {
  return (
    <BrowserFrame url={url} className={cn('h-[480px] w-[760px]', className)}>
      <div className="flex h-[442px] bg-white">
        <nav className="flex w-[52px] shrink-0 flex-col items-center gap-1.5 border-e border-[#ebe7e1] bg-[#f7f6f4] py-3">
          <span className="mb-2 flex h-8 w-8 items-center justify-center rounded-[9px] bg-[#202020]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M4 18L12 5l8 13"
                stroke="#f7f6f4"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          {RAIL_ITEMS.map(({ key, Icon }) => (
            <span
              key={key}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-[9px]',
                key === active ? 'bg-[#e6f0ff] text-[#202020]' : 'text-[#a8a298]',
              )}
            >
              <Icon className="h-[15px] w-[15px]" strokeWidth={key === active ? 2.2 : 1.8} />
            </span>
          ))}
        </nav>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-[44px] shrink-0 items-center justify-between border-b border-[#ebe7e1] px-4">
            <div className="flex min-w-0 items-baseline gap-2">
              <span className="text-[13.5px] font-semibold text-[#202020]">{title}</span>
              {subtitle && (
                <span className="truncate text-[11px] text-[#8a857c]">{subtitle}</span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {actions}
              <span className="flex h-7 items-center gap-1.5 rounded-full border border-[#ebe7e1] px-2.5 text-[10.5px] text-[#202020]">
                <MapPin className="h-3 w-3 text-[#8a857c]" />
                {location ?? DEFAULT_LOCATION[locale]}
                <ChevronDown className="h-3 w-3 text-[#8a857c]" />
              </span>
              <Avatar initials="RN" tone={2} size={26} className="ring-0" />
            </div>
          </header>
          <div className="relative min-h-0 flex-1">{children}</div>
        </div>
      </div>
    </BrowserFrame>
  );
}
